import { useMutation } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { api, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { PROVIDER_LABEL, providerPath } from '../lib/integrations';
import type { AsmIntegration, IntegrationProvider, IntuneIntegration, JamfProIntegration, JamfSchoolIntegration, MosyleIntegration } from '../lib/types';
import { CampusNetworkNote } from './campus-network';
import { DataSourcePicker, useIntegrationMutations } from './integration-shell';
import { useToast } from './toast';
import { Alert, Button, ConfirmDialog, Field, Input, Switch, Textarea } from './ui';

export function Step({ number, title, children }: { number: number; title: string; children: ReactNode }) {
  return (
    <section className="space-y-4">
      <h3 className="text-sm font-semibold text-slate-900">
        {number}. {title}
      </h3>
      {children}
    </section>
  );
}

export function Instructions({ steps }: { steps: ReactNode[] }) {
  return (
    <ol className="list-decimal space-y-1 pl-5 text-sm text-slate-600">
      {steps.map((step, index) => (
        <li key={index}>{step}</li>
      ))}
    </ol>
  );
}

export function useCredentials(provider: IntegrationProvider) {
  const toast = useToast();
  const { refresh } = useIntegrationMutations(provider);
  const save = useMutation({
    mutationFn: (body: Record<string, string>) => api.put(`/integrations/${providerPath(provider)}/credentials`, body),
    onSuccess: () => {
      refresh();
      toast.success('Credentials saved. Test the connection to make sure they work.');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/integrations/${providerPath(provider)}/credentials`),
    onSuccess: () => {
      refresh();
      toast.success(`Removed ${PROVIDER_LABEL[provider]} credentials`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  return { save, remove };
}

export function RemoveCredentials({
  provider,
  remove,
  description = 'Syncs stop until new credentials are saved. Devices already synced stay in Edvora.',
}: {
  provider: IntegrationProvider;
  remove: ReturnType<typeof useCredentials>['remove'];
  description?: string;
}) {
  const [confirming, setConfirming] = useState(false);
  return (
    <>
      <Button variant="ghost" icon={<Trash2 />} onClick={() => setConfirming(true)}>
        Remove credentials
      </Button>
      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={() => remove.mutate(undefined, { onSettled: () => setConfirming(false) })}
        loading={remove.isPending}
        title={`Remove ${PROVIDER_LABEL[provider]} credentials?`}
        description={description}
        confirmLabel="Remove"
      />
    </>
  );
}

function ServerAddress({ integration, placeholder }: { integration: JamfProIntegration | JamfSchoolIntegration; placeholder: string }) {
  const { can } = useAuth();
  const { update } = useIntegrationMutations(integration.provider);
  const [serverUrl, setServerUrl] = useState(integration.settings.serverUrl ?? '');
  useEffect(() => setServerUrl(integration.settings.serverUrl ?? ''), [integration.settings.serverUrl]);
  return (
    <div className="flex max-w-xl flex-col gap-2 sm:flex-row sm:items-end">
      <div className="flex-1">
        <Field label="Server address">
          <Input value={serverUrl} onChange={(e) => setServerUrl(e.target.value)} disabled={!can('ADMIN')} placeholder={placeholder} className="font-mono text-xs" />
        </Field>
      </div>
      {can('ADMIN') && (
        <Button loading={update.isPending} onClick={() => update.mutate({ settings: { serverUrl } })}>
          Save address
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Jamf Pro
// ---------------------------------------------------------------------------

export function JamfProConnection({ integration }: { integration: JamfProIntegration }) {
  const { can } = useAuth();
  const isAdmin = can('ADMIN');
  const { save, remove } = useCredentials('JAMF_PRO');
  const saved = integration.jamfPro;
  const [clientId, setClientId] = useState(saved.clientId ?? '');
  const [clientSecret, setClientSecret] = useState('');

  return (
    <div className="space-y-8">
      <section>
        <h3 className="mb-3 text-sm font-semibold text-slate-900">Data source</h3>
        <DataSourcePicker integration={integration} />
      </section>

      {integration.mode === 'LIVE' && (
        <>
          <Step number={1} title="Jamf Pro server">
            <ServerAddress integration={integration} placeholder="https://yourschool.jamfcloud.com" />
          </Step>

          <Step number={2} title="API client">
            <Instructions
              steps={[
                <>
                  In Jamf Pro, go to <strong>Settings › System › API roles and clients</strong>.
                </>,
                <>
                  Create an API role with <strong>Read Computers</strong>, <strong>Read Mobile Devices</strong>, <strong>Read Buildings</strong>, and{' '}
                  <strong>Read Sites</strong>. Edvora only reads inventory.
                </>,
                <>Create an API client with that role, turn it on, and generate a client secret.</>,
              ]}
            />
            {saved.secretsUnreadable && <Alert tone="red">The saved credentials can’t be read. Enter them again.</Alert>}
            <div className="grid max-w-2xl gap-4 sm:grid-cols-2">
              <Field label="Client ID">
                <Input value={clientId} onChange={(e) => setClientId(e.target.value)} disabled={!isAdmin} className="font-mono text-xs" />
              </Field>
              <Field label="Client secret">
                <Input
                  type="password"
                  value={clientSecret}
                  onChange={(e) => setClientSecret(e.target.value)}
                  disabled={!isAdmin}
                  placeholder={saved.hasClientSecret ? 'Saved - leave blank to keep' : ''}
                />
              </Field>
            </div>
            {isAdmin && (
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="primary"
                  loading={save.isPending}
                  onClick={() => save.mutate({ clientId, ...(clientSecret ? { clientSecret } : {}) }, { onSuccess: () => setClientSecret('') })}
                >
                  Save API client
                </Button>
                {saved.clientId && <RemoveCredentials provider="JAMF_PRO" remove={remove} />}
              </div>
            )}
          </Step>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Jamf School
// ---------------------------------------------------------------------------

export function JamfSchoolConnection({ integration }: { integration: JamfSchoolIntegration }) {
  const { can } = useAuth();
  const isAdmin = can('ADMIN');
  const { save, remove } = useCredentials('JAMF_SCHOOL');
  const saved = integration.jamfSchool;
  const [networkId, setNetworkId] = useState(saved.networkId ?? '');
  const [apiKey, setApiKey] = useState('');

  return (
    <div className="space-y-8">
      <section>
        <h3 className="mb-3 text-sm font-semibold text-slate-900">Data source</h3>
        <DataSourcePicker integration={integration} />
      </section>

      {integration.mode === 'LIVE' && (
        <>
          <Step number={1} title="Jamf School address">
            <ServerAddress integration={integration} placeholder="https://yourschool.jamfcloud.com" />
          </Step>

          <Step number={2} title="Network ID and API key">
            <Instructions
              steps={[
                <>
                  Find the <strong>Network ID</strong> in Jamf School under <strong>Devices › Enroll Device(s)</strong>.
                </>,
                <>
                  Under <strong>Organization › Settings › API</strong>, add an API key that can read <strong>devices</strong> and <strong>locations</strong>.
                </>,
              ]}
            />
            {saved.secretsUnreadable && <Alert tone="red">The saved credentials can’t be read. Enter them again.</Alert>}
            <div className="grid max-w-2xl gap-4 sm:grid-cols-2">
              <Field label="Network ID">
                <Input value={networkId} onChange={(e) => setNetworkId(e.target.value)} disabled={!isAdmin} className="font-mono text-xs" />
              </Field>
              <Field label="API key">
                <Input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} disabled={!isAdmin} placeholder={saved.hasApiKey ? 'Saved - leave blank to keep' : ''} />
              </Field>
            </div>
            {isAdmin && (
              <div className="flex flex-wrap gap-2">
                <Button variant="primary" loading={save.isPending} onClick={() => save.mutate({ networkId, ...(apiKey ? { apiKey } : {}) }, { onSuccess: () => setApiKey('') })}>
                  Save API key
                </Button>
                {saved.networkId && <RemoveCredentials provider="JAMF_SCHOOL" remove={remove} />}
              </div>
            )}
          </Step>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Apple School Manager
// ---------------------------------------------------------------------------

export function AsmConnection({ integration }: { integration: AsmIntegration }) {
  const { can } = useAuth();
  const isAdmin = can('ADMIN');
  const { save, remove } = useCredentials('APPLE_SCHOOL_MANAGER');
  const saved = integration.appleSchoolManager;
  const [clientId, setClientId] = useState(saved.clientId ?? '');
  const [keyId, setKeyId] = useState(saved.keyId ?? '');
  const [privateKey, setPrivateKey] = useState('');

  return (
    <div className="space-y-8">
      <section>
        <h3 className="mb-3 text-sm font-semibold text-slate-900">Data source</h3>
        <DataSourcePicker integration={integration} />
      </section>

      {integration.mode === 'LIVE' && (
        <Step number={1} title="API account">
          <Instructions
            steps={[
              <>
                Sign in to Apple School Manager as an Administrator or Site Manager, choose your name, then <strong>Preferences › API</strong>.
              </>,
              <>
                Create an API account and <strong>download its private key</strong>. Apple only lets you download it once.
              </>,
              <>
                Choose <strong>Manage</strong> on the account to copy its <strong>Client ID</strong> and <strong>Key ID</strong>.
              </>,
            ]}
          />
          {saved.secretsUnreadable && <Alert tone="red">The saved credentials can’t be read. Enter them again.</Alert>}
          <div className="grid max-w-2xl gap-4 sm:grid-cols-2">
            <Field label="Client ID" hint="Starts with SCHOOLAPI.">
              <Input value={clientId} onChange={(e) => setClientId(e.target.value)} disabled={!isAdmin} className="font-mono text-xs" placeholder="SCHOOLAPI.…" />
            </Field>
            <Field label="Key ID">
              <Input value={keyId} onChange={(e) => setKeyId(e.target.value)} disabled={!isAdmin} className="font-mono text-xs" />
            </Field>
          </div>
          {isAdmin && (
            <div className="max-w-2xl space-y-2">
              <Field label="Private key" hint={saved.hasPrivateKey ? 'A key is saved. Upload a new one only to replace it.' : 'The .pem file Apple School Manager downloaded.'}>
                <input
                  type="file"
                  accept=".pem,.key,.txt"
                  className="text-sm"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (file) setPrivateKey(await file.text());
                  }}
                />
              </Field>
              <Textarea
                rows={3}
                value={privateKey}
                onChange={(e) => setPrivateKey(e.target.value)}
                placeholder="-----BEGIN PRIVATE KEY-----"
                className="font-mono text-xs"
              />
            </div>
          )}
          {isAdmin && (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="primary"
                loading={save.isPending}
                onClick={() => save.mutate({ clientId, keyId, ...(privateKey ? { privateKey } : {}) }, { onSuccess: () => setPrivateKey('') })}
              >
                Save API account
              </Button>
              {saved.clientId && <RemoveCredentials provider="APPLE_SCHOOL_MANAGER" remove={remove} />}
            </div>
          )}
        </Step>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sync options shared by every device integration
// ---------------------------------------------------------------------------

export function DeviceSyncOptions({ integration }: { integration: JamfProIntegration | JamfSchoolIntegration | MosyleIntegration | IntuneIntegration | AsmIntegration }) {
  const { can } = useAuth();
  const isAdmin = can('ADMIN');
  const { update } = useIntegrationMutations(integration.provider);
  const [values, setValues] = useState<Record<string, boolean>>(() => ({ ...integration.settings }) as unknown as Record<string, boolean>);
  const toggle = (key: string, label: string, description: string) => (
    <Switch label={label} description={description} checked={!!values[key]} disabled={!isAdmin} onChange={(checked) => setValues((v) => ({ ...v, [key]: checked }))} />
  );

  return (
    <div className="max-w-xl space-y-6">
      {integration.provider === 'JAMF_SCHOOL' && (
        <CampusNetworkNote>
          Device pages compare the public IP address each device last checked in to Jamf School from with your school’s to show whether it
          was on or off campus.
        </CampusNetworkNote>
      )}
      {integration.provider === 'JAMF_PRO' && (
        <>
          {toggle('syncComputers', 'Sync computers', 'Macs from computer inventory.')}
          {toggle('syncMobileDevices', 'Sync mobile devices', 'iPads, iPhones, and Apple TVs from mobile device inventory.')}
        </>
      )}
      {integration.provider === 'APPLE_SCHOOL_MANAGER' && (
        <>
          {toggle('checkAppleCare', 'Check AppleCare and warranty coverage', 'Looks up coverage for a few hundred devices each sync and refreshes it monthly.')}
          {toggle('fillAssetDetails', 'Fill in purchase details on linked assets', 'Adds the order date, Apple as the vendor, and the coverage end date where an asset has none. Existing values are never changed.')}
        </>
      )}
      {toggle('autoLinkBySerial', 'Link devices to assets by serial number', 'When an asset’s serial matches a device, link them automatically.')}
      {toggle(
        'autoCreateAssets',
        'Create assets for new devices',
        integration.provider === 'APPLE_SCHOOL_MANAGER'
          ? 'Every device Apple School Manager lists gets an asset, including ones not enrolled yet.'
          : 'Devices without a matching asset get one, tagged with the asset tag from Jamf when it’s unique.',
      )}
      {isAdmin && (
        <Button
          variant="primary"
          loading={update.isPending}
          onClick={() => {
            const { serverUrl: _serverUrl, ...settings } = values as Record<string, unknown>;
            update.mutate({ settings });
          }}
        >
          Save sync settings
        </Button>
      )}
    </div>
  );
}
